import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Cookie, ShieldCheck } from 'lucide-react';
import {
  DEFAULT_CHOICES,
  getConsent,
  getChoices,
  setConsent,
  acceptAll,
  rejectAll,
  applyConsent,
  onOpenPreferences,
} from '../lib/consent';

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [choices, setChoicesState] = useState(DEFAULT_CHOICES);

  // Initial mount: apply stored consent (or nothing), decide banner visibility
  useEffect(() => {
    const stored = getConsent();
    if (!stored) {
      setVisible(true);
    } else {
      setChoicesState(stored.choices);
      applyConsent(stored.choices);
    }
    const off = onOpenPreferences(() => {
      setChoicesState(getChoices());
      setShowDetails(true);
      setVisible(true);
    });
    return off;
  }, []);

  const closeAndApply = () => {
    setVisible(false);
    setShowDetails(false);
    applyConsent();
  };

  const handleAcceptAll = () => {
    acceptAll();
    closeAndApply();
  };

  const handleRejectAll = () => {
    rejectAll();
    closeAndApply();
  };

  const handleSavePreferences = () => {
    setConsent(choices);
    closeAndApply();
  };

  const toggle = (key) => {
    setChoicesState((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
      aria-labelledby="cookie-banner-title"
      className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6 bg-[#0F0F0F] text-[#FDFBF7] shadow-2xl"
    >
      <div className="max-w-6xl mx-auto">
        <button
          type="button"
          onClick={closeAndApply}
          aria-label="Close banner (essentials only)"
          data-testid="cookie-banner-close"
          className="absolute top-3 right-3 md:top-4 md:right-4 text-[#9A9A9A] hover:text-[#FDFBF7]"
        >
          <X size={18} />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <Cookie size={22} className="text-[#C5A880] flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h2 id="cookie-banner-title" className="font-heading text-lg text-[#FDFBF7] mb-2">
              We use cookies
            </h2>
            <p className="text-sm text-[#9A9A9A] leading-relaxed max-w-3xl">
              Essential cookies keep the site running. With your consent, we also use analytics and marketing cookies to improve gate33.co and measure the impact of our content. You can accept, refuse or customise your choice — you can change it anytime via our <Link to="/legal/cookies" className="underline text-[#C5A880]">Cookie Policy</Link>.
            </p>
          </div>
        </div>

        {showDetails && (
          <div className="border-t border-[#3A3A3A] pt-4 mb-4 space-y-4" data-testid="cookie-banner-details">
            <PreferenceRow
              testId="cookie-pref-essential"
              icon={<ShieldCheck size={16} className="text-[#C5A880]" />}
              title="Essential"
              description="Required for the site to work. Always active."
              enabled={true}
              locked
            />
            <PreferenceRow
              testId="cookie-pref-analytics"
              title="Analytics"
              description="Anonymous audience measurement via Google Analytics 4."
              enabled={choices.analytics}
              onToggle={() => toggle('analytics')}
            />
            <PreferenceRow
              testId="cookie-pref-marketing"
              title="Marketing"
              description="Conversion tracking via LinkedIn Insight and HubSpot."
              enabled={choices.marketing}
              onToggle={() => toggle('marketing')}
            />
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          {!showDetails ? (
            <>
              <button
                type="button"
                onClick={() => setShowDetails(true)}
                data-testid="cookie-banner-customize"
                className="text-xs uppercase tracking-[0.15em] font-semibold px-5 py-3 border border-[#5A5A5A] text-[#FDFBF7] hover:border-[#C5A880] hover:text-[#C5A880] transition-colors"
              >
                Customise
              </button>
              <button
                type="button"
                onClick={handleRejectAll}
                data-testid="cookie-banner-reject-all"
                className="text-xs uppercase tracking-[0.15em] font-semibold px-5 py-3 border border-[#5A5A5A] text-[#FDFBF7] hover:border-[#C5A880] hover:text-[#C5A880] transition-colors"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                data-testid="cookie-banner-accept-all"
                className="text-xs uppercase tracking-[0.15em] font-semibold px-6 py-3 bg-[#C5A880] text-[#0F0F0F] hover:bg-[#FDFBF7] transition-colors"
              >
                Accept all
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleRejectAll}
                data-testid="cookie-banner-details-reject"
                className="text-xs uppercase tracking-[0.15em] font-semibold px-5 py-3 border border-[#5A5A5A] text-[#FDFBF7] hover:border-[#C5A880] hover:text-[#C5A880] transition-colors"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={handleSavePreferences}
                data-testid="cookie-banner-save"
                className="text-xs uppercase tracking-[0.15em] font-semibold px-6 py-3 bg-[#C5A880] text-[#0F0F0F] hover:bg-[#FDFBF7] transition-colors"
              >
                Save preferences
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const PreferenceRow = ({ testId, icon, title, description, enabled, locked, onToggle }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex items-start gap-2">
      {icon}
      <div>
        <div className="text-sm text-[#FDFBF7] font-semibold">{title}</div>
        <div className="text-xs text-[#9A9A9A] mt-0.5">{description}</div>
      </div>
    </div>
    <button
      type="button"
      onClick={onToggle}
      disabled={locked}
      data-testid={testId}
      aria-pressed={enabled}
      aria-label={`${title} — ${enabled ? 'enabled' : 'disabled'}`}
      className={`relative w-11 h-6 flex-shrink-0 rounded-full transition-colors ${
        enabled ? 'bg-[#C5A880]' : 'bg-[#3A3A3A]'
      } ${locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-[#FDFBF7] transition-transform ${
          enabled ? 'translate-x-5' : ''
        }`}
      />
    </button>
  </div>
);

export default CookieBanner;
